import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { useProperties } from "@/contexts/PropertyContext";
import { formatCurrency } from "@/lib/format";
import { Plus, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["clean", "dirty", "occupied", "maintenance", "blocked", "out_of_order"];

export default function Rooms() {
    const { t } = useTranslation();
    const { properties, activePropertyId } = useProperties();
    const [rooms, setRooms] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // null | room-doc

    const load = async () => {
        const params = new URLSearchParams();
        if (activePropertyId !== "all") params.set("property_id", activePropertyId);
        const { data } = await api.get(`/rooms?${params}`);
        setRooms(data);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [activePropertyId]);

    const filtered = statusFilter === "all" ? rooms : rooms.filter((r) => r.status === statusFilter);
    const counts = STATUSES.reduce((acc, s) => { acc[s] = rooms.filter((r) => r.status === s).length; return acc; }, {});

    const updateStatus = async (room, status) => {
        try {
            await api.patch(`/rooms/${room.id}`, { status });
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const deleteRoom = async (room) => {
        if (!window.confirm(t("rooms.confirm_delete", { number: room.number, defaultValue: `Fshi dhomën ${room.number}?` }))) return;
        try {
            await api.delete(`/rooms/${room.id}`);
            toast.success(t("rooms.toast_deleted", { defaultValue: "Dhoma u fshi" }));
            await load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <>
            <Topbar
                title={t("rooms.title")}
                subtitle={t("rooms.subtitle_count", { count: rooms.length })}
                actions={<button onClick={() => setOpen(true)} className="akm-btn-primary" data-testid="new-room-btn"><Plus size={14} /> {t("rooms.add_room")}</button>}
            />
            <div className="flex-1 overflow-auto p-6">
                {/* Status filters */}
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label={t("rooms.status.all")} count={rooms.length} />
                    {STATUSES.map((s) => (
                        <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={t(`rooms.status.${s}`)} count={counts[s]} statusKey={s} />
                    ))}
                </div>

                {/* Grid */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8" data-testid="rooms-grid">
                    {filtered.map((r) => (
                        <div key={r.id} className="akm-card p-3" data-testid={`room-card-${r.number}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-base font-semibold tracking-tight">{r.number}</div>
                                    <div className="text-[11px] text-muted-foreground">{r.room_type}</div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <StatusBadge status={r.status} />
                                </div>
                            </div>
                            <div className="mt-3 text-[11px] text-muted-foreground">
                                {r.capacity} {t("rooms.pax")} · {formatCurrency(r.base_price)}/{t("rooms.per_night_short")}
                            </div>
                            <select
                                value={r.status}
                                onChange={(e) => updateStatus(r, e.target.value)}
                                className="akm-input mt-2 h-7 text-[11px]"
                                data-testid={`room-status-${r.number}`}
                            >
                                {STATUSES.map((s) => <option key={s} value={s}>{t(`rooms.status.${s}`)}</option>)}
                            </select>
                            <div className="mt-2 flex items-center justify-end gap-1">
                                <button
                                    onClick={() => setEditing(r)}
                                    data-testid={`room-edit-${r.number}`}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                                    title={t("common.edit", { defaultValue: "Ndrysho" })}
                                ><Pencil size={12} /></button>
                                <button
                                    onClick={() => deleteRoom(r)}
                                    data-testid={`room-delete-${r.number}`}
                                    className="inline-flex h-6 w-6 items-center justify-center rounded text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                                    title={t("common.delete", { defaultValue: "Fshi" })}
                                ><Trash2 size={12} /></button>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full rounded-[10px] border border-dashed border-border bg-card/50 py-12 text-center text-sm text-muted-foreground">{t("rooms.no_rooms_found")}</div>
                    )}
                </div>
            </div>
            {open && <RoomForm properties={properties} onClose={() => setOpen(false)} onSaved={async () => { setOpen(false); await load(); }} />}
            {editing && <RoomEditForm room={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
        </>
    );
}

function FilterChip({ active, onClick, label, count, statusKey }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium capitalize transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-muted/60"}`}
            data-testid={`room-filter-${statusKey || "all"}`}
        >
            {label}
            <span className={`rounded-full px-1.5 text-[10px] ${active ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground"}`}>{count}</span>
        </button>
    );
}

function RoomForm({ properties, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        property_id: properties[0]?.id || "",
        number: "",
        room_type: "Standard",
        capacity: 2,
        base_price: 80,
        status: "clean",
    });
    // Keep property_id in sync when properties finish loading after modal mount.
    // Fixes a race where the <select> visually shows the first option but
    // React state still holds "" -> backend gets property_id="" -> 404.
    useEffect(() => {
        if (!form.property_id && properties?.length > 0) {
            setForm((f) => ({ ...f, property_id: properties[0].id }));
        }
    }, [properties, form.property_id]);
    const [saving, setSaving] = useState(false);
    const [dupError, setDupError] = useState(null); // {message, existing_id, existing_number}
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setDupError(null);
        try {
            await api.post("/rooms", { ...form, capacity: Number(form.capacity), base_price: Number(form.base_price) });
            toast.success(t("rooms.toast_created"));
            onSaved();
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            if (detail && detail.code === "duplicate_room_number") {
                setDupError(detail);
            } else if (detail && detail.code === "invalid_room_number") {
                setDupError({ message: detail.message });
            } else {
                toast.error(formatApiErrorDetail(detail));
            }
        }
        finally { setSaving(false); }
    };
    return (
        <Modal title={t("rooms.form_title")} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3">
                <Field label={t("rooms.field_property")}>
                    <select className="akm-input" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}>
                        {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Field label={t("rooms.field_number")}>
                            <input
                                className={`akm-input ${dupError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                required
                                value={form.number}
                                onChange={(e) => { setForm({ ...form, number: e.target.value }); setDupError(null); }}
                                data-testid="room-create-number"
                            />
                        </Field>
                        {dupError && (
                            <div className="mt-1 text-[11px] text-red-600" data-testid="room-number-dup-error">
                                {dupError.message}
                                {dupError.existing_number && (
                                    <span className="ml-1 text-muted-foreground">
                                        ({t("rooms.existing_number", "ekziston")} <span className="font-mono">{dupError.existing_number}</span>)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <Field label={t("rooms.field_type")}><input className="akm-input" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={t("rooms.field_capacity")}><input type="number" min="1" className="akm-input" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
                    <Field label={t("rooms.field_base_price")}><input type="number" step="0.01" className="akm-input" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="room-create-submit">{saving ? t("common.saving") : t("common.create")}</button>
                </div>
            </form>
        </Modal>
    );
}

export function Modal({ title, onClose, children, width = "max-w-md" }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-foreground/30" onClick={onClose} />
            <div className={`relative w-full ${width} akm-card p-5`}>
                <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-tight">{title}</div>
                    <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted/60"><X size={14} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

function RoomEditForm({ room, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        number: room.number || "",
        room_type: room.room_type || "",
        capacity: room.capacity || 2,
        base_price: room.base_price || 0,
    });
    const [saving, setSaving] = useState(false);
    const [dupError, setDupError] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setDupError(null);
        try {
            await api.patch(`/rooms/${room.id}`, {
                number: form.number,
                room_type: form.room_type,
                capacity: Number(form.capacity),
                base_price: Number(form.base_price),
            });
            toast.success(t("rooms.toast_updated", { defaultValue: "Dhoma u përditësua" }));
            onSaved();
        } catch (e2) {
            const detail = e2.response?.data?.detail;
            if (detail && (detail.code === "duplicate_room_number" || detail.code === "invalid_room_number")) {
                setDupError(detail);
            } else {
                toast.error(formatApiErrorDetail(detail));
            }
        }
        finally { setSaving(false); }
    };
    return (
        <Modal title={t("rooms.edit_title", { defaultValue: `Ndrysho dhomën ${room.number}` })} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="room-edit-form">
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Field label={t("rooms.field_number")}>
                            <input
                                className={`akm-input ${dupError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                required
                                value={form.number}
                                onChange={(e) => { setForm({ ...form, number: e.target.value }); setDupError(null); }}
                                data-testid="room-edit-number"
                            />
                        </Field>
                        {dupError && (
                            <div className="mt-1 text-[11px] text-red-600" data-testid="room-edit-number-dup-error">
                                {dupError.message}
                                {dupError.existing_number && (
                                    <span className="ml-1 text-muted-foreground">
                                        ({t("rooms.existing_number", "ekziston")} <span className="font-mono">{dupError.existing_number}</span>)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <Field label={t("rooms.field_type")}><input className="akm-input" value={form.room_type} onChange={(e) => setForm({ ...form, room_type: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Field label={t("rooms.field_capacity")}><input type="number" min="1" className="akm-input" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
                    <Field label={t("rooms.field_base_price")}><input type="number" step="0.01" className="akm-input" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></Field>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="akm-btn-ghost">{t("common.cancel")}</button>
                    <button type="submit" disabled={saving} className="akm-btn-primary" data-testid="room-edit-save">{saving ? t("common.saving") : t("common.save", { defaultValue: "Ruaj" })}</button>
                </div>
            </form>
        </Modal>
    );
}
